/**
 * LandingIntakeControls — iter87.80.a
 *
 * Extracted from SiteAdminLeagueRequests so both the new Rivalry
 * Subscriptions tab AND the legacy Requests tab (safety net) can render
 * the same source of truth for:
 *   - Rivalry requests intake toggle (paused/open — the "Landing gate")
 *   - Per-tier acceptance (free + trial). Premium removed per iter87.80
 *     since Turnstile-gated free auto-approval + Pro+ manual grant model
 *     covers the surface today.
 */
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Unlock, Lock } from "lucide-react";
import { api } from "@/lib/api";


export default function LandingIntakeControls() {
    const [requestsOpen, setRequestsOpen] = useState(true);
    const [tierOpen, setTierOpen] = useState({ free: true, trial: true });
    const [busy, setBusy] = useState(false);
    const [tierBusy, setTierBusy] = useState({});

    useEffect(() => {
        (async () => {
            try {
                const r = await api.getSiteSettings();
                // iter87.179 — Fixed field-name bug. API returns
                // `league_requests_open`, not `requests_open`. Prior
                // code silently defaulted `requestsOpen` to `true` on
                // every mount, letting the UI diverge from actual DB
                // state (bit prod hard — DB had false, UI showed OPEN,
                // toggle clicks flipped local state but the PATCH was
                // sending `{requests_open: ...}` which Pydantic
                // dropped, so the DB never changed).
                if (typeof r?.league_requests_open === "boolean") setRequestsOpen(r.league_requests_open);
                if (r?.tier_requests_open) setTierOpen({
                    free: r.tier_requests_open.free !== false,
                    trial: r.tier_requests_open.trial !== false,
                });
            } catch { /* silent — inherits site default */ }
        })();
    }, []);

    const toggleIntake = async () => {
        setBusy(true);
        try {
            const next = !requestsOpen;
            const r = await api.updateSiteSettings({ league_requests_open: next });
            setRequestsOpen(r?.league_requests_open ?? next);
            toast.success(next ? "Landing form re-opened" : "Landing form paused");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
        setBusy(false);
    };

    const toggleTier = async (tk) => {
        setTierBusy(prev => ({ ...prev, [tk]: true }));
        try {
            const next = !tierOpen[tk];
            const r = await api.updateSiteSettings({ tier_requests_open: { [tk]: next } });
            if (r?.tier_requests_open) setTierOpen({
                free: r.tier_requests_open.free !== false,
                trial: r.tier_requests_open.trial !== false,
            });
            toast.success(`${tk.toUpperCase()} tier ${next ? "open" : "paused"}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
        setTierBusy(prev => ({ ...prev, [tk]: false }));
    };

    return (
        <div className="space-y-3" data-testid="landing-intake-controls">
            {/* Site-wide intake toggle */}
            <div
                data-testid="Rivalry-requests-intake-toggle"
                className={`brutal-card p-4 flex flex-wrap items-center justify-between gap-3 ${requestsOpen ? "border-cyber/40" : "border-blaze/60 bg-blaze/5"}`}
            >
                <div className="flex items-start gap-3 min-w-0">
                    <div className={`w-9 h-9 border-2 border-black shadow-brutal flex items-center justify-center shrink-0 ${requestsOpen ? "bg-cyber" : "bg-blaze"}`}>
                        {requestsOpen ? <Unlock className="w-4 h-4 text-black" strokeWidth={2.5} /> : <Lock className="w-4 h-4 text-black" strokeWidth={2.5} />}
                    </div>
                    <div className="min-w-0">
                        <p className="text-[10px] uppercase font-bold tracking-widest text-zinc-500">Rivalry requests intake</p>
                        <p className="font-bold text-base" data-testid="Rivalry-requests-intake-status">
                            {requestsOpen ? "Open — Landing form accepting requests" : "Paused — Landing shows \"requests paused\" copy"}
                        </p>
                        <p className="text-[11px] text-zinc-500 leading-relaxed mt-0.5">
                            Toggles the public "Request Rivalry" flow. When paused, the Landing form is replaced with a closed message; direct-API submissions still go through and get the alt "thanks, we're paused" copy.
                        </p>
                    </div>
                </div>
                <button
                    data-testid="Rivalry-requests-intake-toggle-btn"
                    onClick={toggleIntake}
                    disabled={busy}
                    className={`brutal-btn text-xs py-2 ${requestsOpen ? "bg-blaze text-white" : "bg-cyber text-black"} disabled:opacity-50`}
                >
                    {busy ? "Saving..." : requestsOpen ? "Pause intake" : "Open intake"}
                </button>
            </div>

            {/* Per-tier acceptance — two tiers, side-by-side */}
            {requestsOpen && (
                <div className="brutal-card p-4 space-y-2" data-testid="tier-acceptance-toggles">
                    <div>
                        <p className="text-[10px] uppercase font-bold tracking-widest text-zinc-500">Per-tier acceptance</p>
                        <p className="text-[11px] text-zinc-500 leading-relaxed mt-0.5">
                            Pause specific tiers without taking the whole Landing form offline. Useful while sorting out partner partnerships or billing.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {["free", "trial"].map((tk) => {
                            const open = tierOpen[tk] !== false;
                            const bBusy = !!tierBusy[tk];
                            return (
                                <div
                                    key={tk}
                                    data-testid={`tier-toggle-card-${tk}`}
                                    className={`brutal-card-light p-3 flex items-center justify-between gap-2 ${open ? "border-cyber/40" : "border-heat/40"}`}
                                >
                                    <div>
                                        <p className="font-bold uppercase text-xs">{tk === "trial" ? "Comp (Trial)" : "Free"}</p>
                                        <p className="text-[10px] text-zinc-500">
                                            {open ? "Accepting signups" : "Paused"}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        disabled={bBusy}
                                        onClick={() => toggleTier(tk)}
                                        data-testid={`tier-toggle-${tk}-btn`}
                                        className={`pill text-[10px] font-bold uppercase border-2 ${open ? "border-cyber text-cyber" : "border-heat text-heat"} disabled:opacity-50`}
                                    >
                                        {bBusy ? "..." : open ? "On" : "Off"}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
